import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const BookWriting = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const services = [
    {
      title: 'Ghostwriting',
      description: 'Professional writers bring your story to life while maintaining your unique voice and vision.',
      features: ['Complete confidentiality', 'Full rights ownership', 'Unlimited revisions']
    },
    {
      title: 'Manuscript Development',
      description: 'From concept to completion, we develop compelling narratives with strong plot structures.',
      features: ['Plot development', 'Character creation', 'World-building']
    },
    {
      title: 'Genre Expertise',
      description: 'Specialized writers for every genre including fiction, non-fiction, memoir, and business books.',
      features: ['Fiction & Non-fiction', 'Memoir & Biography', 'Business & Self-help']
    },
    {
      title: 'Collaborative Process',
      description: 'Work closely with experienced writers through every chapter and milestone.',
      features: ['Regular updates', 'Chapter-by-chapter review', 'Direct communication']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Consultation & Outline Creation',
      description: 'We start with an in-depth consultation to understand your vision, goals, and target audience. Together, we create a comprehensive outline and chapter structure for your book.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png'
    },
    {
      step: '02',
      title: 'Chapter Development & Submission',
      description: 'Our expert writers craft your manuscript chapter by chapter. You receive regular submissions for review, providing feedback to ensure the content aligns with your vision.',
      icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png'
    },
    {
      step: '03',
      title: 'Editing, Formatting & Finalization',
      description: 'We polish your manuscript with professional editing, format it to industry standards, and deliver your completed book ready for publishing.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png',
      title: 'Expert Writers',
      description: 'Published authors and professional writers with proven track records across all genres.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png',
      title: 'Your Voice, Your Story',
      description: 'We capture your unique voice and vision while bringing professional writing expertise.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png',
      title: 'Timely Delivery',
      description: 'Structured timeline with milestone-based delivery to keep your project on track.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png',
      title: 'Quality Guarantee',
      description: 'Unlimited revisions until you\'re completely satisfied with every chapter.'
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to write a book?',
      answer: 'The timeline varies based on book length and complexity. Typically, a 50,000-word book takes 3-6 months from start to finish. We provide a detailed timeline after our initial consultation based on your specific project requirements.'
    },
    {
      question: 'Will I own the rights to my book?',
      answer: 'Yes, absolutely. You retain 100% ownership and all rights to your book. Our ghostwriters work under a complete confidentiality agreement, and upon completion, all intellectual property rights belong to you.'
    },
    {
      question: 'How involved will I be in the writing process?',
      answer: 'You can be as involved as you want. We typically recommend reviewing and approving each chapter before moving forward, but we\'re flexible. Some clients prefer weekly updates, while others trust us to deliver completed sections for review.'
    },
    {
      question: 'What genres do you specialize in?',
      answer: 'Our team includes writers specializing in fiction (literary, thriller, romance, sci-fi, fantasy), non-fiction (business, self-help, memoir, biography), and specialized topics. We match you with a writer who has expertise in your specific genre.'
    },
    {
      question: 'How much does book writing cost?',
      answer: 'Pricing depends on book length, complexity, research requirements, and timeline. We offer customized quotes after understanding your project. Contact us for a free consultation and detailed proposal tailored to your needs.'
    },
    {
      question: 'Can you help with just part of my book?',
      answer: 'Yes! Whether you need help completing a manuscript you\'ve started, rewriting specific chapters, or developing a particular section, we offer flexible services to meet your needs.'
    },
    {
      question: 'What happens if I\'m not satisfied with the writing?',
      answer: 'We offer unlimited revisions on each chapter until you\'re completely satisfied. Your approval is required before we move to the next section, ensuring the manuscript aligns with your vision throughout the process.'
    },
    {
      question: 'Do you provide research for non-fiction books?',
      answer: 'Yes, our writers conduct thorough research for non-fiction projects, including interviews, data gathering, and fact-checking. We ensure all information is accurate, well-sourced, and properly cited where necessary.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      title: 'Memoir Author',
      text: 'The ghostwriter understood my story deeply and brought it to life beautifully. They captured my voice perfectly while adding professional polish I couldn\'t achieve alone.',
      rating: 5
    },
    {
      name: 'James Peterson',
      title: 'Business Book Author',
      text: 'Outstanding service from start to finish. The writer transformed my business concepts into a compelling, readable book that\'s resonating with my audience.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      title: 'Fiction Author',
      text: 'I had the plot in mind but struggled with execution. Their fiction specialist brought my characters to life and created a page-turner. Couldn\'t be happier!',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PROFESSIONAL BOOK WRITING</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Transform Your Ideas Into a <span className="text-amber-700">Published Book</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert ghostwriters and authors ready to bring your story to life. Whether you have a complete concept or just an idea, we'll craft a compelling manuscript that captures your unique voice and resonates with readers.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  Get Started Today
                </Button>
                <Button 
                  onClick={() => scrollToSection('process')}
                  className="bg-white hover:bg-gray-50 text-amber-700 border-2 border-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  See How It Works
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">500+</div>
                  <div className="text-gray-600 text-sm">Books Written</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">98%</div>
                  <div className="text-gray-600 text-sm">Client Satisfaction</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">15+</div>
                  <div className="text-gray-600 text-sm">Genres Covered</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">50+</div>
                  <div className="text-gray-600 text-sm">Expert Writers</div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=600&fit=crop" 
                  alt="Professional book writing"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img 
                        src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                        alt="Books published"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">500+</div>
                      <div className="text-sm text-gray-600">Books Published</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 right-12 w-40 h-40 bg-amber-300 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Image + Content Section - How We Help */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Image */}
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&h=600&fit=crop" 
                alt="Writing process"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-700/20 to-transparent rounded-2xl"></div>
            </div>

            {/* Right Content */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">HOW WE HELP</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                From Idea to <span className="text-amber-700">Bestseller</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We understand that every author's journey is unique. Whether you're a first-time writer or an experienced author looking for professional support, our team is here to transform your vision into a compelling manuscript.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/3524/3524715.png" 
                      alt="Professional Excellence"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Excellence</h3>
                    <p className="text-gray-600">Work with published authors and industry professionals who understand the craft.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/2490/2490402.png" 
                      alt="Your Voice Matters"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Your Voice Matters</h3>
                    <p className="text-gray-600">We ensure your unique perspective and voice shine through every page.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/1087/1087927.png" 
                      alt="Complete Support"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Support</h3>
                    <p className="text-gray-600">From first draft to final manuscript, we're with you every step of the way.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                Start Your Journey
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Creative CTA Section 1 */}
      <section className="py-20 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Tell Your Story?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join hundreds of successful authors who trusted us with their manuscripts. Your book journey starts with a simple conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                Schedule Free Consultation
              </Button>
              <Button 
                onClick={() => scrollToSection('process')}
                className="bg-transparent hover:bg-white/10 text-white border-2 border-white font-bold py-6 px-10 text-lg rounded-full transition-all"
              >
                Learn Our Process
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Genre Expertise Section with Images */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Expert Writers for <span className="text-amber-700">Every Genre</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our specialized writers have deep expertise across all major genres and writing styles.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&h=400&fit=crop" 
                alt="Fiction writing"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                <div className="p-6 text-white w-full">
                  <h3 className="text-2xl font-bold mb-2">Fiction</h3>
                  <p className="text-gray-200 mb-4">Literary, Thriller, Romance, Sci-Fi & More</p>
                  <Button 
                    onClick={() => setIsModalOpen(true)}
                    className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-full text-sm"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=600&h=400&fit=crop" 
                alt="Non-fiction writing"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                <div className="p-6 text-white w-full">
                  <h3 className="text-2xl font-bold mb-2">Non-Fiction</h3>
                  <p className="text-gray-200 mb-4">Business, Self-Help, History & Biography</p>
                  <Button 
                    onClick={() => setIsModalOpen(true)}
                    className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-full text-sm"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all">
              <img 
                src="https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?w=600&h=400&fit=crop" 
                alt="Memoir writing"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                <div className="p-6 text-white w-full">
                  <h3 className="text-2xl font-bold mb-2">Memoir</h3>
                  <p className="text-gray-200 mb-4">Personal Stories & Life Experiences</p>
                  <Button 
                    onClick={() => setIsModalOpen(true)}
                    className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-full text-sm"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive <span className="text-amber-700">Writing Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial concept to final manuscript, we provide everything you need to create a professional, compelling book.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section - Modern Design */}
      <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It <span className="text-amber-700">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A simple, transparent process designed to bring your book to life with minimal hassle and maximum quality.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Connecting Line - Desktop Only */}
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200" style={{ top: '80px' }}></div>

              {process.map((item, index) => (
                <div key={index} className="relative">
                  {/* Step Card */}
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-amber-500 relative z-10">
                    {/* Large Step Number with Gradient */}
                    <div className="absolute -top-6 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">{item.step}</span>
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>

                    {/* Arrow Indicator - Desktop Only */}
                    {index < process.length - 1 && (
                      <div className="hidden md:block absolute -right-4 top-20 text-amber-500">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Mobile Arrow */}
                  {index < process.length - 1 && (
                    <div className="md:hidden flex justify-center py-4">
                      <svg className="w-8 h-8 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l3.293-3.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Below Process */}
          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">Ready to get started with your book?</p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-6 px-12 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Start Your Book Journey
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WHY CHOOSE US</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-amber-700">Rushmore Publishing</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We combine professional expertise with a personal touch to deliver exceptional results. Our commitment to quality and client satisfaction sets us apart.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=800&h=600&fit=crop" 
                alt="Professional writing team"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-amber-700 text-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold mb-2">98%</div>
                <div className="text-sm">Client Satisfaction Rate</div>
                <div className="mt-3 text-xs opacity-90">Based on 500+ completed projects</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Creative CTA Section 2 - Split Design */}
      <section className="py-0 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl max-w-6xl mx-auto">
            {/* Left Side - Image */}
            <div className="relative h-96 lg:h-auto">
              <img 
                src="https://images.unsplash.com/photo-1471107340929-a87cd0f5b5f3?w=800&h=600&fit=crop" 
                alt="Start writing your book"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-amber-900/60 to-transparent"></div>
            </div>

            {/* Right Side - CTA Content */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-12 flex flex-col justify-center">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Your Story Deserves to be Told
              </h3>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                Don't let your ideas remain unwritten. Our expert writers are ready to help you create a manuscript that captivates readers and stands the test of time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-4 px-8 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  Start Writing Today
                </Button>
                <Button 
                  className="bg-white/10 hover:bg-white/20 text-white border-2 border-white font-bold py-4 px-8 text-lg rounded-full transition-all"
                >
                  Call: 1-800-BOOK-NOW
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-500" />
                  <span>No obligation</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-500" />
                  <span>Free consultation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section with Background Image */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=1920&h=800&fit=crop" 
            alt="Start your book journey"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-8">
              <span className="text-white text-sm font-semibold tracking-wider">LIMITED SPOTS AVAILABLE</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Write Your Book?
            </h2>
            <p className="text-xl mb-12 opacity-90 leading-relaxed">
              Let's bring your story to life. Schedule a free consultation to discuss your book project with our expert team. No pressure, just possibilities.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
              >
                Get Your Free Consultation
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto pt-12 border-t border-white/20">
              <div>
                <div className="text-3xl font-bold mb-2">500+</div>
                <div className="text-sm opacity-80">Books Completed</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">50+</div>
                <div className="text-sm opacity-80">Expert Writers</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">98%</div>
                <div className="text-sm opacity-80">Satisfaction Rate</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-80">Support Available</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-amber-700">Authors Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-amber-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-amber-700">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all"
                >
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookWriting;
